import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, useLocation } from 'react-router-dom';
import { 
  Bell, Settings, Search, ChevronDown, LogOut, 
  UserCircle, Menu, X, Home, Grid, User, MessageSquare, 
  Menu as MenuIcon, Package, Wrench, 
  Users, ScanLine, DollarSign, ShoppingCart, Monitor, ClipboardCheck, BookOpen,
  ArrowLeft, ArrowRight, Settings2, Briefcase,
  PanelLeftClose, PanelLeftOpen, BarChart2, Activity, Palette, GraduationCap, ShoppingBag,
  Sun, Moon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeProvider, useTheme } from './ThemeContext';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ProtectedRoute, PublicOnlyRoute } from './auth/RouteGuards';
import LoginPage from './components/auth/LoginPage';

// Import components
import MainDashboard from './components/MainDashboard';
import LyncsNeoImmersive from './components/immersive/LyncsNeoImmersive';
import ProjectsDashboard from './components/Projects/ProjectsDashboard';
import ProjectDetail from './components/Projects/ProjectDetail';
import ProjectForm from './components/Projects/ProjectForm';
import Inventory from './modules/Inventory/Inventory';
import AuditDashboard from './modules/Audit/AuditDashboard';
import CreateAudit from './modules/Audit/CreateAudit';
import AuditDetails from './modules/Audit/AuditDetails';
import StoreAudits from './modules/Audit/StoreAudits';
import AuditForm from './modules/Audit/AuditForm';
import BackButton from './components/BackButton';
import COGSDashboard from './modules/COGS/COGSDashboard';

// Archive module imports
import ArchiveDashboard from './modules/Archive/ArchiveDashboard';
import DocumentDetail from './modules/Archive/DocumentDetail';
import UploadDocument from './modules/Archive/UploadDocument';

// Import Maintenance components
import Maintenance from './modules/Maintenance/Maintenance';

// Import Batch Control components
import BatchControlDashboard from './modules/BatchControl/BatchControlDashboard';
import BatchForm from './modules/BatchControl/BatchForm';
import BatchDetail from './modules/BatchControl/BatchDetail';
import BatchReport from './modules/BatchControl/BatchReport';
import BatchScanner from './modules/BatchControl/BatchScanner';

// Import new HR module components
import HRDashboard from './modules/HR/HRDashboard';
import EmployeeDetail from './modules/HR/EmployeeDetail';
import EmployeeForm from './modules/HR/EmployeeForm';

// Import SOP module components
import SOPLayout from './modules/SOP/components/layout/SOPLayout';
import SOPDashboard from './modules/SOP/components/SOPDashboard';
import RecipesTab from './modules/SOP/components/recipes/RecipesTab';
import ManualsTab from './modules/SOP/components/manuals/ManualsTab';
import ChecklistsTab from './modules/SOP/components/checklists/ChecklistsTab';
import AnnouncementsTab from './modules/SOP/components/announcements/AnnouncementsTab';
import RecipeDetail from './modules/SOP/components/recipes/RecipeDetail';
import CreateRecipe from './modules/SOP/components/recipes/CreateRecipe';
import ManualDetail from './modules/SOP/components/manuals/ManualDetail';
import CreateManual from './modules/SOP/components/manuals/CreateManual';
import ChecklistDetail from './modules/SOP/components/checklists/ChecklistDetail';
import CreateChecklist from './modules/SOP/components/checklists/CreateChecklist';
import AnnouncementDetail from './modules/SOP/components/announcements/AnnouncementDetail';
import CreateAnnouncement from './modules/SOP/components/announcements/CreateAnnouncement';

// Import ITSupport module
import ITSupport from './modules/ITSupport/ITSupport';

// Import AssetsDashboard component
import Assets from './modules/Assets/Assets';

// Import Tracker module
import Tracker from './modules/Tracker/Tracker';

// Import Pulse module
import PulseDashboard from './modules/Pulse/PulseDashboard';

// Import Scorecard module
import Scorecard from './modules/Scorecard/Scorecard';

// Import Supply Chain module
import SupplyChainDashboard from './modules/SupplyChain/SupplyChainDashboard';

// Import SCMS module
import SCMSDashboard from './modules/SCMS/SCMSDashboard';

// Import Bloom module
import BloomDashboard from './modules/Bloom/BloomDashboard';
import CourseForm from './modules/Bloom/components/CourseForm';
import LearnerManagement from './modules/Bloom/components/LearnerManagement';
import CertificateManagement from './modules/Bloom/components/CertificateManagement';

// Import Creatives module
import CreativesDashboard from './modules/Creatives/CreativesDashboard';

// Import Chains module
import Chains from './modules/Chains';
import ChainsDashboard from './modules/Chains/components/dashboard/ChainsDashboard';
import PurchaseRequests from './modules/Chains/components/purchase-requests/PurchaseRequests';
import Procurement from './modules/Chains/components/procurement/Procurement';
import Logistics from './modules/Chains/components/logistics/Logistics';
import ChainsWarehouse from './modules/Chains/components/warehouse/Warehouse';
import Planning from './modules/Chains/components/planning/Planning';
import Reports from './modules/Chains/components/reports/Reports';
import ChainsSettings from './modules/Chains/components/settings/Settings';

// Import Warehouse module
import WarehouseDashboard from './modules/Warehouse/WarehouseDashboard';

// Import Logistics module
import LogisticsDashboard from './modules/Logistics/LogisticsDashboard';

// Import Res (Real Estate) module
import ResDashboard from './modules/Res/ResDashboard';

// Import Projex module
import Projex from './modules/Projex';
import ProjexDashboard from './modules/Projex/components/dashboard/ProjexDashboard';
import ProjectList from './modules/Projex/components/projects/ProjectList';
import Timeline from './modules/Projex/components/timeline/Timeline';
import Documents from './modules/Projex/components/documents/Documents';
import Team from './modules/Projex/components/team/Team';
import Analytics from './modules/Projex/components/analytics/Analytics';
import ProjexSettings from './modules/Projex/components/settings/Settings';

// Import Financial module
import FinancialDashboard from './modules/Financial/FinancialDashboard';

// Import Sales module
import SalesDashboard from './modules/Sales/SalesDashboard';

// Search Modal Component
const SearchModal = ({ isOpen, onClose, searchQuery, setSearchQuery }) => {
  const { isDarkMode } = useTheme();
  if (!isOpen) return null;
  
  return (
    <div className={`fixed inset-0 backdrop-blur-xl z-50 ${
      isDarkMode ? 'bg-black/80' : 'bg-white/80'
    }`}>
      <div className={`relative group fixed inset-x-0 top-0 backdrop-blur-md border-b p-4 shadow-lg ${
        isDarkMode 
          ? 'bg-black/20 border-white/10' 
          : 'bg-white/95 border-slate-200'
      }`}>
        {/* Animated Background Glow - only in dark mode */}
        {isDarkMode && (
          <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
        )}
        <div className="relative z-10">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${
            isDarkMode ? 'text-white/40' : 'text-slate-400'
          }`} />
          <input
            autoFocus
            type="text"
            placeholder="Summon data or modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full h-12 pl-10 pr-4 backdrop-blur-xl border rounded-lg text-base focus:outline-none focus:ring-2 ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white placeholder:text-white/20 focus:ring-indigo-500/20 focus:border-indigo-500' 
                : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500'
            }`}
          />
          <button
            onClick={onClose}
            className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
            }`}
          >
            <X className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Header Components
const NotificationBell = ({ count }) => {
  const { isDarkMode } = useTheme();
  return (
    <button className={`relative p-2 rounded-lg transition-all duration-300 group ${
      isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
    }`}>
      <Bell className={`h-5 w-5 transition-colors ${
        isDarkMode ? 'text-white/60 group-hover:text-white' : 'text-slate-600 group-hover:text-slate-900'
      }`} />
      {count > 0 && (
        <span className="absolute top-0 right-0 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center transform translate-x-1 group-hover:scale-110 transition-transform">
          {count}
        </span>
      )}
    </button>
  );
};

const UserProfile = ({ user, onLogout }) => {
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-3 p-2 rounded-xl transition-colors ${
          isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
        }`}
      >
        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-lg">
          {user.name[0]}
        </div>
        <div className="hidden md:block text-left">
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{user.role}</p>
        </div>
        <ChevronDown className={`hidden md:block h-4 w-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
      </button>

      {isOpen && (
        <div className={`absolute right-0 mt-2 w-64 rounded-xl shadow-xl border py-2 z-50 ${
          isDarkMode 
            ? 'bg-white/10 backdrop-blur-xl border-white/20' 
            : 'bg-white border-slate-200'
        }`}>
          <div className={`px-4 py-2 border-b ${
            isDarkMode ? 'border-white/10' : 'border-slate-200'
          }`}>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.email}</p>
            <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>Signed in as {user.role}</p>
          </div>
          <button className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${
            isDarkMode 
              ? 'text-white/80 hover:bg-white/10' 
              : 'text-slate-700 hover:bg-slate-50'
          }`}>
            <UserCircle className="mr-2 h-4 w-4" />
            View Profile
          </button>
          <button className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${
            isDarkMode 
              ? 'text-white/80 hover:bg-white/10' 
              : 'text-slate-700 hover:bg-slate-50'
          }`}>
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </button>
          <div className={`h-px my-2 ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`} />
          <button
            onClick={() => {
              setIsOpen(false);
              onLogout?.();
            }}
            className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${
            isDarkMode 
              ? 'text-red-400 hover:bg-red-500/20' 
              : 'text-red-600 hover:bg-red-50'
          }`}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

// Mobile Menu Component
const MobileMenu = ({ user, onLogout }) => {
  const { isDarkMode } = useTheme();
  return (
    <div className="px-4 py-2 space-y-4">
      <div className="flex items-center">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold shadow-lg">
          {user.name[0]}
        </div>
        <div className="ml-3">
          <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
          <p className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{user.role}</p>
        </div>
      </div>
      <div className="space-y-1">
        <button className={`w-full px-3 py-2 text-sm rounded-lg flex items-center transition-colors ${
          isDarkMode 
            ? 'text-white/80 hover:bg-white/10' 
            : 'text-slate-700 hover:bg-slate-50'
        }`}>
          <UserCircle className="mr-2 h-4 w-4" />
          View Profile
        </button>
        <button className={`w-full px-3 py-2 text-sm rounded-lg flex items-center transition-colors ${
          isDarkMode 
            ? 'text-white/80 hover:bg-white/10' 
            : 'text-slate-700 hover:bg-slate-50'
        }`}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </button>
        <button
          onClick={onLogout}
          className={`w-full px-3 py-2 text-sm rounded-lg flex items-center transition-colors ${
          isDarkMode 
            ? 'text-red-400 hover:bg-red-500/20' 
            : 'text-red-600 hover:bg-red-50'
        }`}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </button>
      </div>
    </div>
  );
};
// Placeholder components for other modules
const ProcurementDashboard = () => (
  <div>
    <BackButton />
    <h1 className="text-2xl font-bold mt-4">Procurement Dashboard</h1>
  </div>
);

const StoreAuditsDashboard = () => (
  <div>
    <BackButton />
    <h1 className="text-2xl font-bold mt-4">Store Audits Dashboard</h1>
  </div>
);

// AppContent component that uses useLocation
const AppContent = ({ searchQuery }) => {
  const location = useLocation();
  
  // For Chains module, render without any constraints
  if (location.pathname.startsWith('/chains')) {
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/chains" element={<Chains />}>
            <Route index element={<ChainsDashboard />} />
            <Route path="purchase-requests" element={<PurchaseRequests />} />
            <Route path="procurement" element={<Procurement />} />
            <Route path="logistics" element={<Logistics />} />
            <Route path="warehouse" element={<ChainsWarehouse />} />
            <Route path="planning" element={<Planning />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<ChainsSettings />} />
          </Route>
        </Routes>
      </div>
    );
  }

  // For Creatives module, render without any constraints for full-width usage
  if (location.pathname.startsWith('/creatives')) {
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/creatives/*" element={<CreativesDashboard />} />
        </Routes>
      </div>
    );
  }

  // For Projex module, render without any constraints for full-width usage
  if (location.pathname.startsWith('/projex')) {
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/projex" element={<Projex />}>
            <Route index element={<ProjexDashboard />} />
            <Route path="projects" element={<ProjectList />} />
            <Route path="timeline" element={<Timeline />} />
            <Route path="documents" element={<Documents />} />
            <Route path="team" element={<Team />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<ProjexSettings />} />
          </Route>
        </Routes>
      </div>
    );
  }

  // For Lyncs Neo immersive mode, render as full-screen experience
  if (location.pathname.startsWith('/immersive-neo')) {
    return (
      <div className="min-h-screen">
        <Routes>
          <Route path="/immersive-neo" element={<LyncsNeoImmersive />} />
        </Routes>
      </div>
    );
  }
  
  // Dashboard gets full-width layout; other modules use constrained layout
  const isDashboard = location.pathname === '/';
  return (
    <main className={isDashboard ? 'w-full min-h-screen pb-24' : 'py-6 px-4 sm:px-6 lg:px-8 pb-24 max-w-7xl mx-auto'}>
      <Routes>
        <Route path="/" element={<MainDashboard searchQuery={searchQuery} />} />
        
        {/* Projects routes */}
        <Route path="/projects" element={<ProjectsDashboard />} />
        <Route path="/projects/new" element={<ProjectForm />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/projects/:id/edit" element={<ProjectForm />} />
        
        {/* Audit routes */}
        <Route path="/audit" element={<AuditDashboard />} />
        <Route path="/audit/new" element={<AuditForm />} />
        <Route path="/create-audit" element={<CreateAudit />} />
        <Route path="/audit/:id" element={<AuditDetails />} />
        <Route path="/store-audits" element={<StoreAudits />} />
        
        {/* Other module routes */}
        <Route path="/procurement" element={<ProcurementDashboard />} />
        <Route path="/assets/*" element={<Assets />} />
        <Route path="/warehouse" element={<WarehouseDashboard />} />

        {/* Logistics Module Routes */}
        <Route path="/logistics" element={<LogisticsDashboard />} />
        
        {/* Res (Real Estate) Module Routes */}
        <Route path="/res" element={<ResDashboard />} />
        
        <Route path="/it-support/*" element={<ITSupport />} />
        <Route path="/store-audits" element={<StoreAuditsDashboard />} />
        <Route path="/inventory" element={<Inventory />} />

        {/* COGS routes */}
        <Route path="/cogs" element={<COGSDashboard />} />

        {/* Archive routes */}
        <Route path="/archive" element={<ArchiveDashboard />} />
        <Route path="/archive/upload" element={<UploadDocument />} />
        <Route path="/archive/:id" element={<DocumentDetail />} />

        {/* Maintenance routes */}
        <Route path="/maintenance/*" element={<Maintenance />} />

        {/* HR Module Routes */}
        <Route path="/hr" element={<HRDashboard />} />
        <Route path="/hr/employees" element={<HRDashboard />} />
        <Route path="/hr/employees/new" element={<EmployeeForm />} />
        <Route path="/hr/employees/:id" element={<EmployeeDetail />} />
        <Route path="/hr/employees/:id/edit" element={<EmployeeForm />} />

        {/* Batch Control routes */}
        <Route path="/batch-control" element={<BatchControlDashboard />} />
        <Route path="/batch-control/new" element={<BatchForm />} />
        <Route path="/batch-control/:id" element={<BatchDetail />} />
        <Route path="/batch-control/:id/report" element={<BatchReport />} />
        <Route path="/batch-control/scanner" element={<BatchScanner />} />

        {/* SOP Module Routes */}
        <Route path="/sop" element={<SOPLayout />}>
          <Route index element={<SOPDashboard />} />
          <Route path="recipes" element={<RecipesTab />} />
          <Route path="recipes/:id" element={<RecipeDetail />} />
          <Route path="recipes/new" element={<CreateRecipe />} />
          <Route path="manuals" element={<ManualsTab />} />
          <Route path="manuals/:id" element={<ManualDetail />} />
          <Route path="manuals/new" element={<CreateManual />} />
          <Route path="checklists" element={<ChecklistsTab />} />
          <Route path="checklists/:id" element={<ChecklistDetail />} />
          <Route path="checklists/new" element={<CreateChecklist />} />
          <Route path="announcements" element={<AnnouncementsTab />} />
          <Route path="announcements/:id" element={<AnnouncementDetail />} />
          <Route path="announcements/new" element={<CreateAnnouncement />} />
        </Route>

        {/* Tracker routes */}
        <Route path="/tracker/*" element={<Tracker />} />

        {/* Pulse routes */}
        <Route path="/pulse/*" element={<PulseDashboard />} />

        {/* Scorecard routes */}
        <Route path="/scorecard/*" element={<Scorecard />} />

        {/* Supply Chain routes */}
        <Route path="/supply-chain/*" element={<SupplyChainDashboard />} />

        {/* SCMS routes */}
        <Route path="/scms/*" element={<SCMSDashboard />} />

        {/* Bloom Module Routes */}
        <Route path="/bloom" element={<BloomDashboard />} />
        <Route path="/bloom/courses/new" element={<CourseForm />} />
        <Route path="/bloom/courses/:id/edit" element={<CourseForm />} />
        <Route path="/bloom/learners" element={<LearnerManagement />} />
        <Route path="/bloom/certificates" element={<CertificateManagement />} />

        {/* Financial Module Routes */}
        <Route path="/financial/*" element={<FinancialDashboard />} />

        {/* Sales Module Routes */}
        <Route path="/sales/*" element={<SalesDashboard />} />
        <Route path="/immersive-neo" element={<LyncsNeoImmersive />} />
      </Routes>
    </main>
  );
};

// BottomMenuWrapper component that uses useLocation
const BottomMenuWrapper = ({ user }) => {
  const location = useLocation();
  return !location.pathname.startsWith('/chains')
    && !location.pathname.startsWith('/creatives')
    && !location.pathname.startsWith('/projex')
    && !location.pathname.startsWith('/immersive-neo')
    ? <BottomMenu user={user} />
    : null;
};

// Add this new component for the shortcuts menu
const ShortcutsMenu = ({ isOpen, onClose, availableModules, selectedShortcuts, onUpdate }) => {
  const { isDarkMode } = useTheme();
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 backdrop-blur-sm z-50 ${
        isDarkMode ? 'bg-black/50' : 'bg-white/50'
      }`}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        onClick={e => e.stopPropagation()}
        className={`absolute bottom-24 left-1/2 -translate-x-1/2 w-[250px]
          backdrop-blur-xl rounded-xl shadow-lg border ${
            isDarkMode 
              ? 'bg-white/10 border-white/20' 
              : 'bg-white/95 border-slate-200/50'
          }`}
      >
        {/* Arrow pointer */}
        <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 
          transform rotate-45 border-r border-b ${
            isDarkMode 
              ? 'bg-white/10 border-white/20' 
              : 'bg-white border-slate-200/50'
          }`} />

        <div className="relative p-3">
          <div className="grid grid-cols-4 gap-2">
            {availableModules.map((module) => (
              <div
                key={module.id}
                className={`flex flex-col items-center p-2 rounded-lg 
                  transition-all duration-200 group ${
                    isDarkMode 
                      ? 'hover:bg-white/10' 
                      : 'hover:bg-slate-50'
                  }`}
              >
                <div className="relative">
                  <div className={`p-2 rounded-lg ${module.bgColor} bg-opacity-10 
                    transition-all duration-200 group-hover:scale-110`}>
                    <module.icon className={`h-5 w-5 ${module.iconColor}`} />
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedShortcuts.includes(module.id)}
                    onChange={(e) => {
                      if (e.target.checked && selectedShortcuts.length < 5) {
                        onUpdate([...selectedShortcuts, module.id]);
                      } else if (!e.target.checked) {
                        onUpdate(selectedShortcuts.filter(id => id !== module.id));
                      }
                    }}
                    disabled={!selectedShortcuts.includes(module.id) && selectedShortcuts.length >= 5}
                    className={`absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full 
                      text-blue-600 focus:ring-0 ${
                        isDarkMode 
                          ? 'border-white/30' 
                          : 'border-slate-300'
                      }`}
                  />
                </div>
                <span className={`mt-1 text-[10px] font-medium text-center
                  group-hover:text-blue-600 ${
                    isDarkMode 
                      ? 'text-white/60 group-hover:text-blue-400' 
                      : 'text-slate-600 group-hover:text-blue-600'
                  }`}>
                  {module.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Add this constant for available modules
const availableModules = [
  {
    id: 'projects',
    label: 'Projects',
    icon: Briefcase,
    link: '/projects',
    bgColor: 'bg-blue-500',
    iconColor: 'text-blue-500'
  },
  {
    id: 'audit',
    label: 'Audit',
    icon: ClipboardCheck,
    link: '/audit',
    bgColor: 'bg-violet-500',
    iconColor: 'text-violet-500'
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Package,
    link: '/inventory',
    bgColor: 'bg-amber-500',
    iconColor: 'text-amber-500'
  },
  {
    id: 'maintenance',
    label: 'Maintenance',
    icon: Wrench,
    link: '/maintenance',
    bgColor: 'bg-rose-500',
    iconColor: 'text-rose-500'
  },
  {
    id: 'hr',
    label: 'HR',
    icon: Users,
    link: '/hr',
    bgColor: 'bg-emerald-500',
    iconColor: 'text-emerald-500'
  },
  {
    id: 'batch-control',
    label: 'Batch Control',
    icon: ScanLine,
    link: '/batch-control',
    bgColor: 'bg-cyan-500',
    iconColor: 'text-cyan-500'
  },
  {
    id: 'cogs',
    label: 'COGS',
    icon: DollarSign,
    link: '/cogs',
    bgColor: 'bg-green-500',
    iconColor: 'text-green-500'
  },
  {
    id: 'procurement',
    label: 'Procurement',
    icon: ShoppingCart,
    link: '/procurement',
    bgColor: 'bg-pink-500',
    iconColor: 'text-pink-500'
  },
  {
    id: 'it-support',
    label: 'IT Support',
    icon: Monitor,
    link: '/it-support',
    bgColor: 'bg-indigo-500',
    iconColor: 'text-indigo-500'
  },
  {
    id: 'sop',
    label: 'SOP',
    icon: BookOpen,
    link: '/sop',
    bgColor: 'bg-teal-500',
    iconColor: 'text-teal-500'
  },
  {
    id: 'pulse',
    label: 'Pulse',
    icon: Activity,
    link: '/pulse',
    bgColor: 'bg-rose-500',
    iconColor: 'text-rose-500'
  },
  {
    id: 'scorecard',
    label: 'Scorecard',
    icon: BarChart2,
    link: '/scorecard',
    bgColor: 'bg-indigo-500',
    iconColor: 'text-indigo-500'
  },
  {
    id: 'supply-chain',
    label: 'Supply Chain',
    icon: Package,
    link: '/supply-chain',
    bgColor: 'bg-emerald-500',
    iconColor: 'text-emerald-500'
  },
  {
    id: 'scms',
    label: 'SCMS',
    icon: Package,
    link: '/scms',
    bgColor: 'bg-indigo-500',
    iconColor: 'text-indigo-500'
  },
  {
    id: 'creatives',
    label: 'Creatives',
    icon: Palette,
    link: '/creatives',
    bgColor: 'bg-purple-500',
    iconColor: 'text-white'
  },
  {
    id: 'chains',
    label: 'Chains',
    icon: Package,
    link: '/chains',
    bgColor: 'bg-gradient-to-r from-blue-500 to-purple-600',
    iconColor: 'text-white'
  },
  {
    id: 'bloom',
    label: 'Bloom',
    icon: GraduationCap,
    link: '/bloom',
    bgColor: 'bg-gradient-to-r from-purple-600 to-pink-600',
    iconColor: 'text-white'
  },
  {
    id: 'financial',
    label: 'Financial',
    icon: DollarSign,
    link: '/financial',
    bgColor: 'bg-gradient-to-r from-green-500 to-emerald-600',
    iconColor: 'text-white'
  },
  {
    id: 'sales',
    label: 'Sales',
    icon: ShoppingBag,
    link: '/sales',
    bgColor: 'bg-gradient-to-r from-teal-500 to-cyan-600',
    iconColor: 'text-white'
  }
];

// Add this new component for the bottom menu
const BottomMenu = ({ user }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [showApps, setShowApps] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const notificationCount = 5;
  const messageCount = 3;
  const [isMobile, setIsMobile] = useState(false);
  const [userShortcuts, setUserShortcuts] = useState(() => {
    const saved = localStorage.getItem('userShortcuts');
    return saved ? JSON.parse(saved) : [];
  });
  const appsButtonRef = useRef(null);
  const [appsPosition, setAppsPosition] = useState({ x: 0, y: 0, width: 0, menuLeft: 0 });
  const [isRetracted, setIsRetracted] = useState(false);

  // Save shortcuts to localStorage when they change
  useEffect(() => {
    localStorage.setItem('userShortcuts', JSON.stringify(userShortcuts));
  }, [userShortcuts]);

  // Base menu items (navigation)
  const navigationItems = [
    { icon: ArrowLeft, label: 'Back', onClick: () => window.history.back() },
    { icon: Home, label: 'Home', link: '/' },
    { icon: Grid, label: 'Apps', onClick: () => setShowApps(true) },
  ];

  // System menu items (always present)
  const systemItems = [
    { icon: MessageSquare, label: 'Messages', link: '/messages', count: messageCount },
    { icon: Bell, label: 'Notifications', link: '/notifications', count: notificationCount },
    { icon: User, label: 'Profile', link: '/profile' },
    { icon: Settings2, label: 'Shortcuts', onClick: () => setShowShortcuts(true) },
    { icon: ArrowRight, label: 'Forward', onClick: () => window.history.forward() },
  ];

  // Get active shortcuts
  const activeShortcuts = availableModules.filter(
    module => userShortcuts.includes(module.id)
  );

  // Combine all items: navigation + shortcuts + system
  const menuItems = [
    ...navigationItems,
    ...activeShortcuts.map(module => ({
      icon: module.icon,
      label: module.label,
      link: module.link,
    })),
    ...systemItems,
  ];

  // Add mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to check if a link is active
  const isActiveRoute = (link) => {
    if (!link) return false;
    if (link === '/') return location.pathname === '/';
    return location.pathname.startsWith(link);
  };

  // Add this function to update the apps position
  const updateAppsPosition = () => {
    if (appsButtonRef.current) {
      const rect = appsButtonRef.current.getBoundingClientRect();
      setAppsPosition({
        x: rect.x,
        y: rect.y,
        width: rect.width
      });
    }
  };

  // Update position when apps menu is opened
  useEffect(() => {
    if (showApps) {
      updateAppsPosition();
    }
  }, [showApps]);

  // Update the menu item rendering in both desktop and mobile views
  const renderMenuItem = (item) => (
    <Link
      key={item.label}
      to={item.link || '#'}
      onClick={(e) => {
        if (item.onClick) {
          e.preventDefault();
          item.onClick();
        }
      }}
      ref={item.label === 'Apps' ? appsButtonRef : null}
      className="relative group flex flex-col items-center"
    >
      {/* Notification Badge */}
      {item.count > 0 && (
        <motion.span
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          className="absolute -top-1 -right-1 z-10 w-[18px] h-[18px] bg-red-500
            text-white text-[11px] font-medium rounded-full flex items-center justify-center"
        >
          {item.count}
        </motion.span>
      )}

      {/* Icon Container */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`p-2 rounded-lg transition-all duration-200 
          ${isActiveRoute(item.link)
            ? 'bg-gradient-to-br from-indigo-400 to-cyan-500'
            : (isDarkMode 
                ? 'bg-white/10 hover:bg-white/20' 
                : 'bg-slate-100 hover:bg-slate-200')
          }
        `}
      >
        <item.icon className={`w-[21px] h-[21px] transition-colors duration-200
          ${isActiveRoute(item.link)
            ? 'text-white'
            : (isDarkMode 
                ? 'text-white/60 group-hover:text-white' 
                : 'text-slate-600 group-hover:text-slate-900')
          }`}
        />
      </motion.div>

      {/* Label */}
      <span className={`text-[11px] font-medium mt-1 transition-colors duration-200
        ${isActiveRoute(item.link)
          ? 'text-white'
          : (isDarkMode 
              ? 'text-white/60 group-hover:text-white' 
              : 'text-slate-600 group-hover:text-slate-900')
        }`}
      >
        {item.label}
      </span>
    </Link>
  );

  return (
    <>
      {/* Responsive Menu */}
      {isMobile ? (
        <div className="fixed bottom-4 right-4 z-40">
          <AnimatePresence>
            {isExpanded ? (
              // Expanded Mobile Menu
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`relative group fixed inset-x-4 bottom-16 backdrop-blur-xl 
                  rounded-[2.5rem] shadow-lg border overflow-hidden ${
                    isDarkMode 
                      ? 'bg-white/10 border-white/20' 
                      : 'bg-white/95 border-slate-200'
                  }`}
              >
                {/* Animated Background Glow - only in dark mode */}
                {isDarkMode && (
                  <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
                )}
                <div className="relative z-10 p-4">
                  {/* Close Button */}
                  <button
                    onClick={() => setIsExpanded(false)}
                    className={`absolute -top-3 -right-3 p-2 backdrop-blur-xl rounded-xl 
                      shadow-sm border ${
                        isDarkMode 
                          ? 'bg-white/10 border-white/20 hover:bg-white/20' 
                          : 'bg-white border-slate-200 hover:bg-slate-100'
                      }`}
                  >
                    <X className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
                  </button>

                  {/* Mobile Grid Layout */}
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-4">
                    {menuItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.link || '#'}
                        onClick={(e) => {
                          if (item.onClick) {
                            e.preventDefault();
                            item.onClick();
                          }
                          setIsExpanded(false);
                        }}
                        className="flex flex-col items-center gap-1"
                      >
                        <div className={`p-3 rounded-xl transition-all duration-200 
                          ${isActiveRoute(item.link)
                            ? 'bg-gradient-to-br from-indigo-400 to-cyan-500'
                            : (isDarkMode 
                                ? 'bg-white/10 hover:bg-white/20' 
                                : 'bg-slate-100 hover:bg-slate-200')
                          }`}
                        >
                          <item.icon className={`w-5 h-5 
                            ${isActiveRoute(item.link)
                              ? 'text-white'
                              : (isDarkMode ? 'text-white/60' : 'text-slate-600')
                            }`}
                          />
                        </div>
                        <span className={`text-[10px] font-medium ${
                          isDarkMode ? 'text-white/60' : 'text-slate-600'
                        }`}>
                          {item.label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>
            ) : (
              // Toggle Button
              <motion.button
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsExpanded(true)}
                className={`relative group p-3 backdrop-blur-xl rounded-xl 
                  shadow-lg border overflow-hidden ${
                    isDarkMode 
                      ? 'bg-white/10 border-white/20 hover:bg-white/20' 
                      : 'bg-white border-slate-200 hover:bg-slate-100'
                  }`}
              >
                {/* Animated Background Glow - only in dark mode */}
                {isDarkMode && (
                  <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
                )}
                <Menu className={`w-5 h-5 relative z-10 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center">
          <AnimatePresence mode="wait">
            {!isRetracted ? (
              <motion.div 
                key="expanded"
                initial={{ width: 40, opacity: 0 }}
                animate={{ 
                  width: 'auto', 
                  opacity: 1,
                  transition: { 
                    width: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.2 }
                  }
                }}
                exit={{ 
                  width: 40,
                  opacity: 0,
                  transition: { 
                    width: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                    opacity: { duration: 0.2 }
                  }
                }}
                className={`relative group backdrop-blur-2xl rounded-3xl shadow-2xl border relative overflow-hidden ${
                  isDarkMode 
                    ? 'bg-black/40 border-white/10' 
                    : 'bg-white/95 border-slate-200'
                }`}
              >
                {/* Animated Background Glow - only in dark mode */}
                {isDarkMode && (
                  <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
                )}
                
                {/* Retract Toggle Button */}
                <motion.button
                  layout
                  onClick={() => setIsRetracted(true)}
                  className={`absolute right-0 top-1/2 -translate-y-1/2 p-2
                    backdrop-blur-xl rounded-xl shadow-sm border transition-colors group z-10 ${
                      isDarkMode 
                        ? 'bg-white/10 border-white/20 hover:bg-white/20' 
                        : 'bg-slate-100 border-slate-200 hover:bg-slate-200'
                    }`}
                >
                  <PanelLeftClose className={`h-4 w-4 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
                </motion.button>

                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ 
                    x: 0, 
                    opacity: 1,
                    transition: { delay: 0.1, duration: 0.2 }
                  }}
                  className="relative z-10 px-5 pr-12 py-2.5"
                >
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    {menuItems.map(renderMenuItem)}
                  </div>
                </motion.div>
              </motion.div>
            ) : (
              <motion.button
                key="retracted"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ 
                  scale: 1, 
                  opacity: 1,
                  transition: { duration: 0.2, ease: 'easeOut' }
                }}
                exit={{ 
                  scale: 0.8, 
                  opacity: 0,
                  transition: { duration: 0.2, ease: 'easeIn' }
                }}
                onClick={() => setIsRetracted(false)}
                className={`relative group p-2 backdrop-blur-2xl rounded-xl shadow-2xl border
                  transition-all duration-200 hover:scale-110 overflow-hidden ${
                    isDarkMode 
                      ? 'bg-black/40 border-white/10 hover:bg-white/10' 
                      : 'bg-white border-slate-200 hover:bg-slate-100'
                  }`}
              >
                {/* Animated Background Glow - only in dark mode */}
                {isDarkMode && (
                  <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
                )}
                <PanelLeftOpen className={`h-4 w-4 relative z-10 ${isDarkMode ? 'text-white' : 'text-slate-600'}`} />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Apps Menu Overlay */}
      <AnimatePresence>
        {showApps && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 backdrop-blur-xl z-50 ${
              isDarkMode ? 'bg-black/80' : 'bg-white/80'
            }`}
            onClick={() => setShowApps(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              style={{
                position: 'absolute',
                bottom: window.innerHeight - appsPosition.y + 16,
                left: appsPosition.x - 125,
                width: '250px',
              }}
              className={`relative group backdrop-blur-xl rounded-[2rem] shadow-2xl border overflow-hidden ${
                isDarkMode 
                  ? 'bg-white/10 border-white/20' 
                  : 'bg-white border-slate-200'
              }`}
            >
              {/* Animated Background Glow - only in dark mode */}
              {isDarkMode && (
                <div className="absolute -inset-2 bg-gradient-to-br from-indigo-400 to-cyan-500 opacity-20 blur-2xl group-hover:opacity-40 transition-opacity" />
              )}
              
              {/* Arrow pointer */}
              <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 
                backdrop-blur-xl transform rotate-45 border-r border-b ${
                  isDarkMode 
                    ? 'bg-white/10 border-white/20' 
                    : 'bg-white border-slate-200'
                }`} />

              <div className="relative z-10 p-3">
                <div className="grid grid-cols-4 gap-2">
                  {availableModules.map((module, index) => (
                    <Link
                      key={index}
                      to={module.link}
                      onClick={() => setShowApps(false)}
                      className={`flex flex-col items-center p-2 rounded-lg transition-all duration-200 group ${
                        isDarkMode 
                          ? 'hover:bg-white/10' 
                          : 'hover:bg-slate-100'
                      }`}
                    >
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${module.bgColor.replace('bg-', 'from-').replace('-500', '-400')} to-cyan-500
                        transition-all duration-200 group-hover:scale-110`}>
                        <module.icon className="h-5 w-5 text-white" />
                      </div>
                      <span className={`mt-1 text-[10px] font-medium text-center ${
                        isDarkMode 
                          ? 'text-white/60 group-hover:text-white' 
                          : 'text-slate-600 group-hover:text-slate-900'
                      }`}>
                        {module.label}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add the ShortcutsMenu */}
      <AnimatePresence>
        {showShortcuts && (
          <ShortcutsMenu
            isOpen={showShortcuts}
            onClose={() => setShowShortcuts(false)}
            availableModules={availableModules}
            selectedShortcuts={userShortcuts}
            onUpdate={(newShortcuts) => {
              setUserShortcuts(newShortcuts);
              // Optionally close the menu if you want
              // setShowShortcuts(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Inner App component that uses theme hook
function AppContentInner({ searchQuery, setSearchQuery }) {
  const { user: authenticatedUser, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const isDashboard = location.pathname === '/';
  const isImmersiveNeo = location.pathname.startsWith('/immersive-neo');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const user = authenticatedUser || {
    name: "Faris Ahmed",
    role: "Administrator",
    email: "faris@company.com"
  };

  // Add event listeners for keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Command/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
      // Escape to close modals
      if (e.key === 'Escape') {
        setIsSearchOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  return (
      <div className={`min-h-screen overflow-hidden font-sans transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-[#050505] text-white selection:bg-indigo-500' 
          : isDashboard 
            ? 'bg-gradient-to-br from-cyan-50 via-blue-50 via-indigo-50 to-violet-50 text-slate-900 selection:bg-indigo-200'
            : 'bg-slate-50 text-slate-900 selection:bg-indigo-200'
      }`}>
          {/* Main Header */}
          {!isImmersiveNeo && (
          <nav className={`fixed top-0 inset-x-0 h-20 z-50 backdrop-blur-md border-b transition-colors duration-300 ${
          isDarkMode 
            ? 'bg-black/20 border-white/5' 
            : 'bg-white/95 border-cyan-200/60'
        }`}>
            <div className="w-full max-w-[1920px] mx-auto px-4 lg:px-8 h-full flex items-center justify-between gap-4">
              {/* Left: Logo */}
              <div className="flex items-center space-x-6 flex-shrink-0">
                <div className="flex items-center space-x-2">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center group cursor-pointer hover:rotate-90 transition-transform duration-500 ${
                    isDarkMode ? 'bg-white' : 'bg-indigo-600'
                  }`}>
                    <Grid className={isDarkMode ? "text-black" : "text-white"} size={20} />
                  </div>
                  <div>
                    <Link to="/" className="flex-shrink-0">
                      <span className={`text-lg font-black tracking-tighter ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}>
                        LYNCS<span className="text-indigo-500">_OS</span>
                      </span>
                    </Link>
                    <div className={`flex items-center space-x-2 text-[10px] font-mono ${
                      isDarkMode ? 'text-white/40' : 'text-slate-600'
                    }`}>
                      <span className="flex items-center"><Bell size={10} className="mr-1" /> ACTIVE</span>
                      <span className="flex items-center"><Settings size={10} className="mr-1" /> SYNC</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Center: Desktop Search */}
              <div className={`hidden md:flex items-center rounded-full px-6 py-2 flex-1 max-w-[500px] transition-all duration-300 mx-4 ${
                isDarkMode 
                  ? 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20' 
                  : 'bg-white/80 border border-slate-200 hover:bg-white hover:border-indigo-300'
              }`}>
                <Search size={18} className={`flex-shrink-0 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
                <input
                  type="text"
                  placeholder="Summon data or modules..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`bg-transparent border-none focus:outline-none ml-4 w-full text-sm ${
                    isDarkMode 
                      ? 'text-white placeholder:text-white/20' 
                      : 'text-slate-900 placeholder:text-slate-400'
                  }`}
                />
                <kbd className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                  isDarkMode ? 'bg-white/10 text-white/40' : 'bg-slate-100 text-slate-500'
                }`}>⌘K</kbd>
              </div>

              {/* Right: Controls */}
              <div className="flex items-center space-x-4 flex-shrink-0">
                <div className="text-right hidden md:block">
                  <p className={`text-sm font-mono font-bold tabular-nums ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}>
                    {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-white/60' : 'text-slate-600'
                  }`}>
                    {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                </div>
                
                {/* Theme Toggle */}
                <button 
                  onClick={toggleTheme}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'hover:bg-white/20' 
                      : 'hover:bg-slate-100'
                  }`}
                  title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDarkMode ? (
                    <Sun className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                  ) : (
                    <Moon className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                  )}
                </button>
                
                <NotificationBell count={3} />
                <button className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
                }`}>
                  <Settings className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                </button>
                <UserProfile user={user} onLogout={logout} />
                
                {/* Mobile Controls */}
                <div className="flex md:hidden items-center space-x-2">
                  <button
                    onClick={toggleTheme}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
                    }`}
                  >
                    {isDarkMode ? (
                      <Sun className="h-5 w-5 text-white/60" />
                    ) : (
                      <Moon className="h-5 w-5 text-slate-600" />
                    )}
                  </button>
                  <button
                    onClick={() => setIsSearchOpen(true)}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
                    }`}
                  >
                    <Search className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                  </button>
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/20' : 'hover:bg-slate-100'
                    }`}
                  >
                    {isMobileMenuOpen ? (
                      <X className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                    ) : (
                      <MenuIcon className={`h-5 w-5 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className={`absolute top-full left-0 right-0 md:hidden backdrop-blur-xl border-t transition-colors ${
                isDarkMode 
                  ? 'bg-black/40 border-white/10' 
                  : 'bg-white/95 border-slate-200'
              }`}>
                <MobileMenu user={user} onLogout={logout} />
              </div>
            )}
          </nav>
          )}

          {/* Search Modal */}
          <SearchModal
            isOpen={isSearchOpen}
            onClose={() => setIsSearchOpen(false)}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />

          {/* Main Content */}
          <div className={isImmersiveNeo ? '' : 'pt-20'}>
            <AppContent searchQuery={searchQuery} />
          </div>

          {/* Add the Bottom Menu - hidden for Chains module */}
          <BottomMenuWrapper user={user} />
        </div>
    );
}

function App() {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={(
                <PublicOnlyRoute>
                  <LoginPage />
                </PublicOnlyRoute>
              )}
            />
            <Route
              path="*"
              element={(
                <ProtectedRoute>
                  <AppContentInner searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                </ProtectedRoute>
              )}
            />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
